import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

import { POST_QUERY_KEY } from './useGetPost';
import { REPLIES_QUERY_KEY } from './useGetReplies';

export type ApiRequest_Create_Reply = {
  content: string;
  parentId?: number;
  metadata?: Record<string, unknown>;
};

export type ApiResponse_Create_Reply = {
  id: number;
  content: string;
  parent_id?: number;
  post_id: number;
  created_at: string;
  updated_at: string;
  author: {
    id: number;
    user: {
      id: string;
      full_name: string;
      email: string;
    };
  };
  _count: {
    replies: number;
    likes: number;
  };
};

const METHOD = 'POST';
const ENDPOINT = 'posts/{postId}/replies';

export const useCreateReply = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      params: { postId: number; communityId?: number; groupId?: number } & ApiRequest_Create_Reply
    ) => {
      try {
        const { postId, communityId, groupId, ...replyData } = params;
        const data = await handleApiCall<ApiResponse_Create_Reply, ApiRequest_Create_Reply>(
          session,
          METHOD,
          getResolvedEndpoint(ENDPOINT, {
            postId: postId.toString(),
          }),
          replyData
        );

        enqueueSnackbar('Reply posted successfully!', { variant: 'success' });
        return data;
      } catch (err) {
        enqueueSnackbar('Failed to post reply.', { variant: 'error' });
        throw err;
      }
    },
    onSuccess: (data, variables) => {
      // Invalidate post replies using correct query key
      queryClient.invalidateQueries({
        queryKey: REPLIES_QUERY_KEY({ 
          postId: variables.postId, 
          page: 1, 
          limit: 50 
        }),
      });
      
      // Invalidate all reply queries for this post
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as unknown[];
          const firstKey = queryKey[0];
          if (Array.isArray(firstKey) && typeof firstKey[1] === 'string') {
            return firstKey[1].includes(`posts/${variables.postId}/replies`);
          }
          return false;
        },
      });
      
      // Invalidate post to update reply count
      queryClient.invalidateQueries({
        queryKey: POST_QUERY_KEY(variables.postId),
      });
      
      // Invalidate post lists to update reply counts
      if (variables.communityId) {
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey as unknown[];
            const firstKey = queryKey[0];
            if (Array.isArray(firstKey) && typeof firstKey[1] === 'string') {
              return firstKey[1].includes(`posts/community/${variables.communityId}`);
            }
            return false;
          },
        });
      }
      if (variables.groupId) {
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey as unknown[];
            const firstKey = queryKey[0];
            if (Array.isArray(firstKey) && typeof firstKey[1] === 'string') {
              return firstKey[1].includes(`posts/group/${variables.groupId}`);
            }
            return false;
          },
        });
      }
    },
  });
};